<script setup lang="ts">
import InfiniteLoading from 'v3-infinite-loading'
import VueMultiselect from 'vue-multiselect'
import queryStringLib from 'query-string'
import { DatePicker } from '@angelblanco/v-calendar'
import { format, parse as dateParse } from 'date-fns'

import type { Presentation, Company, Event } from '@/models/content'
import type { presentationsQueryParams } from '@/models/request'

import { TIME_FORMAT } from '@/constants/app'
import { defaultPresentationsDateRanges, reformatDate } from '@/utils/date'
import { useGoogleAnalyticsPageView } from '~/composables/google-analytics'

import IconRefresh from '@/assets/icons/refresh.svg'
import IconSearch from '@/assets/icons/search.svg'
import IconClose from '@/assets/icons/close.svg'

const arrayFormat = 'bracket'
// repeated constants -> search/index.vue
const SELECT_DATE = 'Select date'
const CUSTOM_DATE = 'Custom date'
const DATE_FORMAT = 'yyyy-MM-dd'
const MAX_DATE = format(Date.now(), DATE_FORMAT)
const MIN_DATE = reformatDate('2016-01-01')
const DEFAULT_DATE_PICKER = { start: MAX_DATE, end: MAX_DATE }

const route = useRoute()
const { $restClient, $seoManager, $rudderstack } = useNuxtApp()

const loading = ref(true)
const loadingScroll = ref(false)
const queryString = ref<string | null>(null)
const queryParams = ref<string | null>(null)
const hasNextPage = ref<boolean>(true)
const isFirstLoad = ref<boolean>(true)
const filteredPresentations = ref<Presentation[]>([])
const totalPresentations = ref<number>(0)
const infiniteScrollKey = ref(0)
const presentationsToShow = ref<Presentation[]>([])
const allCompanies = ref<Company[]>([])
const allEvents = ref<Event[]>([])
const companiesToFilter = ref<Company[]>([])
const eventsToFilter = ref<Event[]>([])
const dateRangeToFilter = ref('')
const query = ref('')
const datePicker = ref(DEFAULT_DATE_PICKER)

const currentParams: presentationsQueryParams = queryStringLib.parse(
  route.fullPath.split('?')[1],
  {
    arrayFormat,
  },
)

const defaultDates = defaultPresentationsDateRanges()
const allDates = ref(Object.keys(defaultDates))
const isDatePickerActive = ref(false)
const dateRange = ref({ start: null, end: null })
const datePickerModelConfig = ref({ type: 'number' })

const seo = $seoManager.generateSeoInfo({
  title: 'Presentations',
})

const formattedCustomDateRange = computed(() => {
  if (!dateRange.value.start || !dateRange.value.end) {
    return SELECT_DATE
  }

  const start = format(new Date(dateRange.value.start), TIME_FORMAT)
  const end = format(new Date(dateRange.value.end), TIME_FORMAT)

  return `${start} - ${end}`
})

useHead(seo)

useGoogleAnalyticsPageView(seo.title)

function setInitialFilters() {
  if (currentParams.companies) {
    companiesToFilter.value = allCompanies.value.filter((company) =>
      currentParams.companies.includes(company.id.toString()),
    )
  }

  if (currentParams.events) {
    eventsToFilter.value = allEvents.value.filter((event) =>
      currentParams.events.includes(event.id.toString()),
    )
  }

  if (currentParams.date_after && currentParams.date_before) {
    dateRange.value = {
      start: dateParse(
        currentParams.date_before,
        DATE_FORMAT,
        new Date(),
      ).valueOf(),
      end: dateParse(
        currentParams.date_after,
        DATE_FORMAT,
        new Date(),
      ).valueOf(),
    }
  }

  if (currentParams.query) {
    query.value = currentParams.query
  }
}

function toggleDatePicker(value) {
  if (!value) {
    clearDatePickers()
    return
  }

  if (value === CUSTOM_DATE) {
    datePicker.value = DEFAULT_DATE_PICKER
    isDatePickerActive.value = !isDatePickerActive.value
    return
  }

  isDatePickerActive.value = false

  if (!allDates.value.includes(value)) return

  dateRange.value.start = defaultDates[value].start
  dateRange.value.end = defaultDates[value].end
  updateRequest()
}

function resetDatePicker() {
  dateRange.value.start = null
  dateRange.value.end = null
  updateRequest()
}

function resetInfiniteScroll() {
  if (loading.value || loadingScroll.value) return
  getPresentationsWithScroll(null, true).then()
  infiniteScrollKey.value += 1
}

async function infiniteScroll($state) {
  if (loading.value || loadingScroll.value) return
  await getPresentationsWithScroll(queryString.value)

  if (hasNextPage.value) $state.loaded()
  else $state.complete()
}

const formatDate = (date?: string): string => {
  if (!date) return ''
  return format(new Date(date), DATE_FORMAT)
}

const formatDates = (start, end) => {
  if (!start || !end) return {}
  return {
    date_after: formatDate(end),
    date_before: formatDate(start),
  }
}

async function setQueryStringParams({
  start,
  end,
  companies,
  events,
  query,
}: {
  start: number
  end: number
  companies: Company[]
  events: Event[]
  query?: string
}) {
  const queryParam = query ? { query } : {}
  const dates = formatDates(start, end)

  const params: presentationsQueryParams = {
    companies: companies.map((company) => company.id),
    events: events.map((event) => event.id),
    ...queryParam,
    ...dates,
  }

  queryParams.value = queryStringLib.stringify(params, { arrayFormat })
  const path = queryParams.value
    ? `${route.path}?${queryParams.value}`
    : route.path
  if (typeof window !== 'undefined') window.history.pushState({}, '', path)
}

const updateRequest = () => {
  const queryParam = query.value ? { query: query.value } : {}
  const params = {
    start: dateRange.value.start,
    end: dateRange.value.end,
    companies: companiesToFilter.value,
    events: eventsToFilter.value,
    ...queryParam,
  }

  setQueryStringParams(params)
  resetInfiniteScroll()
}

const updateSelect = (value) => {
  if (value.length === 0) {
    updateRequest()
  }
}

const timeoutId = ref(null)

const updateInput = (value: string) => {
  clearTimeout(timeoutId.value)
  timeoutId.value = setTimeout(() => {
    if (value && value.length > 1) updateRequest()
  }, 500)
}

const clearInput = () => {
  query.value = ''
  updateRequest()
}

const clearSelect = (callback) => {
  callback()
  updateRequest()
}

const clearDatePickers = () => {
  resetDatePicker()
  dateRangeToFilter.value = ''
}

async function getPresentationsWithScroll(query?: string, reset?: boolean) {
  if (
    (loading.value || loadingScroll.value || !hasNextPage.value) &&
    (!reset || isFirstLoad.value)
  ) {
    return
  }
  loadingScroll.value = true
  isFirstLoad.value = false
  const params = query ? `?${query}&${queryParams.value}` : null
  let queries =
    !params && queryParams.value
      ? `${queryString.value}&${queryParams.value}`
      : queryString.value

  if (reset) {
    const params = queryParams.value ?? queryStringLib.stringify(route.query)
    queries = `?page_limit=12&page_offset=0&show_pagination=true&use_pagination=true&show_total=true&${params}`
  }

  const response = await $restClient.getPresentationsByQueryString({
    queryStrings: queries,
  })

  queryString.value = response.query
  hasNextPage.value = response.hasNextPage
  totalPresentations.value = response.total

  if (!reset) {
    filteredPresentations.value = [
      ...filteredPresentations.value,
      ...response.data,
    ]
  } else {
    filteredPresentations.value = response.data
  }

  presentationsToShow.value = filteredPresentations.value

  setInitialFilters()
  loadingScroll.value = false

  trackSearch()
}

const trackSearch = () => {
  const params = queryStringLib.parse(window.location.search, {
    arrayFormat,
  }) as {
    query?: string
    companies?: Array<string>
    events?: Array<string>
    date_after?: string
    date_before?: string
  }

  if (params.query) {
    $rudderstack.trackSearchDone({
      search_type: 'presentation_search',
      search_query: params.query,
      search_results_count: totalPresentations.value,
    })
  }

  const hasFilters = params.companies || params.events || params.date_after
  const companies = allCompanies.value
    .filter((company) => params.companies?.includes(String(company.id)))
    .map((c) => c.name)
  const events = allEvents.value
    .filter((event) => params.events?.includes(String(event.id)))
    .map((e) => e.name)

  if (hasFilters) {
    $rudderstack.trackFilterApplied({
      filter_values: {
        companies: companies,
        events: events,
        date_after: params.date_after,
        date_before: params.date_before,
      },
      filter_type: 'presentation_search',
      chart_title: 'Presentations',
    })
  }
}

async function getPresentations() {
  const params = queryParams.value ?? queryStringLib.stringify(route.query)

  const queries = params
    ? `?page_limit=12&page_offset=0&show_pagination=true&use_pagination=true&show_total=true&${params}`
    : null

  const response = await $restClient.getPresentationsByQueryString({
    queryStrings: queries,
  })

  queryString.value = response.query
  hasNextPage.value = response.hasNextPage
  totalPresentations.value = response.total
  filteredPresentations.value = [
    ...filteredPresentations.value,
    ...response.data,
  ]

  presentationsToShow.value = filteredPresentations.value

  const companies = await $restClient.getCompanies()
  const events = await $restClient.getEvents()

  allCompanies.value = moveBMToFirstIndex(companies)
  allEvents.value = events

  setInitialFilters()
  loading.value = false
  loadingScroll.value = false
}

const moveBMToFirstIndex = (companies) => {
  const BMIndex = companies.findIndex((company) =>
    company.name.toLowerCase().includes('benchmark'),
  )

  let BMCompany

  if (BMIndex !== -1) {
    BMCompany = companies.splice(BMIndex, 1)[0]
  }

  companies.unshift(BMCompany)

  return companies
}

const updateCustomDate = (value) => {
  dateRange.value = value
  isDatePickerActive.value = false
  updateRequest()
}

onMounted(async () => {
  await getPresentations()
})
</script>

<template>
  <div class="grow">
    <header class="bg-s-100 pb-8 pt-10">
      <div
        class="mx-auto max-w-content-container px-5 lg:mx-auto lg:max-w-screen-lg"
      >
        <div class="mb-9">
          <p class="mb-2 text-xs">
            Showing {{ totalPresentations }}
            {{ totalPresentations === 1 ? 'result' : 'results' }}
          </p>

          <div class="relative">
            <div class="absolute flex h-full items-center">
              <button>
                <IconSearch class="h-5 w-5" />
              </button>
            </div>
            <div v-if="query" class="absolute right-0 flex h-full items-center">
              <button @click="clearInput">
                <IconClose class="h-5 w-5" />
              </button>
            </div>

            <input
              id="presentations-query-input"
              v-model="query"
              type="text"
              class="search-input"
              @keyup="updateInput($event.target.value)"
            />
          </div>
        </div>

        <div
          class="multiselect-container flex flex-col md:flex-row md:gap-4 md:[&>div]:w-[15.625rem]"
        >
          <div>
            <div class="flex items-center justify-between">
              <label for="companies" class="py-2 text-xs font-semibold">
                Company
              </label>
              <IconRefresh
                class="ml-2 mr-1 h-4 w-4 cursor-pointer stroke-black transition-transform active:rotate-45 active:stroke-s-900"
                @click="clearSelect(() => (companiesToFilter = []))"
              />
            </div>
            <VueMultiselect
              id="companies"
              v-model="companiesToFilter"
              :options="allCompanies"
              track-by="name"
              label="name"
              :close-on-select="false"
              :multiple="true"
              :taggable="true"
              placeholder="Select company"
              class="mb-2 rounded border-2 border-s-300"
              data-testid="companies-multiselect"
              @close="updateRequest"
              @update:model-value="updateSelect"
            >
            </VueMultiselect>
          </div>

          <div>
            <div class="flex items-center justify-between">
              <label for="events" class="py-2 text-xs font-semibold">
                Event
              </label>
              <IconRefresh
                class="ml-2 mr-1 h-4 w-4 cursor-pointer stroke-black transition-transform active:rotate-45 active:stroke-s-900"
                @click="clearSelect(() => (eventsToFilter = []))"
              />
            </div>
            <VueMultiselect
              id="events"
              v-model="eventsToFilter"
              :options="allEvents"
              track-by="name"
              label="name"
              :close-on-select="false"
              :multiple="true"
              :taggable="true"
              placeholder="Select event"
              class="mb-2 rounded border-2 border-s-300"
              data-testid="events-multiselect"
              @close="updateRequest"
              @update:model-value="updateSelect"
            >
            </VueMultiselect>
          </div>

          <div class="md:-order-1">
            <div class="flex items-center justify-between">
              <label for="dates" class="py-2 text-xs font-semibold">
                Date
              </label>
              <IconRefresh
                class="ml-2 mr-1 h-4 w-4 cursor-pointer stroke-black transition-transform active:rotate-45 active:stroke-s-900"
                data-testid="multiselect-dates-reset"
                @click="clearDatePickers"
              />
            </div>

            <div class="relative">
              <label
                v-if="formattedCustomDateRange !== SELECT_DATE"
                for="dates"
                class="absolute left-[6px] top-2 z-10 w-[calc(100%-45px)] bg-white p-1 text-xs font-semibold text-s-800"
                data-testid="label-formatted-date-range"
              >
                {{ formattedCustomDateRange }}
              </label>

              <VueMultiselect
                id="dates"
                v-model="dateRangeToFilter"
                :options="allDates"
                :close-on-select="true"
                :multiple="false"
                :taggable="false"
                :placeholder="SELECT_DATE"
                class="mb-2 rounded border-2 border-s-300"
                data-testid="dates-multiselect"
                @update:model-value="toggleDatePicker"
              >
              </VueMultiselect>
            </div>
          </div>
        </div>

        <div class="flex justify-center md:block">
          <div
            class="date-picker-container flex max-w-[250px] flex-col items-center"
            data-testid="date-picker"
          >
            <DatePicker
              v-if="isDatePickerActive"
              id="date-range"
              v-model.range="datePicker"
              :max-date="MAX_DATE"
              :min-date="MIN_DATE"
              range
              :model-config="datePickerModelConfig"
              @update:model-value="updateCustomDate"
            />
          </div>
        </div>
      </div>
    </header>

    <LoadingSpinner
      v-if="$auth.store.token === undefined || loading"
      class="h-[calc(100vh-511px)] md:h-[calc(100vh-387px)] lg:h-[calc(100vh-470px)]"
    />

    <div
      v-else
      class="mt-10 pb-20 lg:pb-36"
      data-testid="presentations-container"
    >
      <section
        class="mx-auto max-w-content-container items-start gap-5 px-5 lg:mx-auto lg:mt-10 lg:flex lg:max-w-screen-lg"
      >
        <div class="flex max-w-content-container flex-grow flex-col gap-5">
          <div
            v-if="!filteredPresentations.length && !loading"
            class="mt-10 text-center md:mt-40"
          >
            Sorry, we couldn't find any results
          </div>
          <div
            class="grid grid-cols-1 gap-5 md:basis-3/4 md:grid-cols-2 lg:grid-cols-3"
            data-testid="presentations-cards-container"
          >
            <PresentationCard
              v-for="presentation in presentationsToShow"
              :id="presentation.id"
              :key="presentation.id"
              :title="presentation.title"
              :published-date="presentation.publishedDate"
              :pills="presentation.tags"
              :cover-url="presentation.coverUrl"
              :file-name="presentation.fileName"
              :company-name="presentation.company.name"
              :event-name="presentation.event.name"
              :link-flip-html5="presentation.linkFlipHTML5"
              :subscription-required="presentation.subscriptionRequired"
              :presentation-data="presentation"
            />
          </div>
          <InfiniteLoading
            :key="infiniteScrollKey"
            :slots="{ complete: ' ', error: ' ' }"
            @infinite="infiniteScroll"
          >
          </InfiniteLoading>
          <LoadingSpinner v-if="loadingScroll" class="h-24" />
        </div>
      </section>
    </div>
  </div>
</template>
